import {
  ControlMonitorEndpointControlSummary,
  getControlName,
  getDefensivePartnerEventCodes,
  getEndpointPartnerEventCodes,
  getPartnerEventName,
  identityProviderPartnerEventCodes,
  isEndpointManagementControl,
  isDefensiveControl,
  isEmailControl,
  isIdentityProviderControl,
  PartnerEvents,
  secondsInDay,
  secondsInHour,
  secondsInMinute,
  ValidControl,
  getControlCategory,
  getControlCategoryName,
  BehavesAsControlCodeMappings,
  isDiscoveredDevicesControl,
  ValidControlCategory,
  isVulnerabilityManagementControl,
  getVulnerabilityPartnerEventCodes,
} from "@theprelude/core";
import { Filter } from "@/lib/filter";
import {
  OdataDefinition,
  OdataOperator,
  OdataValue,
  getOdataId,
} from "@/lib/odata";
import type { ColumnFilter } from "../table/columns";
import { FormFilter, getDefaultCast } from "./filter-form";

const behavesAsControlCodes = new Set([
  ...Object.keys(BehavesAsControlCodeMappings).map(Number),
  ...Object.values(BehavesAsControlCodeMappings),
]);

export function getNamedControl(control?: ValidControl) {
  const controlName = control && getControlName(control.id);
  const categoryName =
    control &&
    behavesAsControlCodes.has(control.id) &&
    getControlCategoryName(getControlCategory(control.id));
  const userControlName = control?.name?.trim();

  return [
    [controlName, categoryName].filter(Boolean).join(" - "),
    userControlName && `(${userControlName})`,
  ]
    .filter(Boolean)
    .join(" ");
}

export function prefixControlColumnLabel(
  label: string,
  control?: ValidControl,
) {
  return [getNamedControl(control), label].filter(Boolean).join(" ");
}

export type AdditionalFilter = ColumnFilter & {
  id: string;
  isArray?: boolean;
};

export const exceptedOdata: OdataDefinition = {
  id: "excepted",
  scope: "excepted",
};

export const exceptedFilter: AdditionalFilter = {
  id: getOdataId(exceptedOdata),
  operators: ["is"],
  type: "boolean",
  label: "Excepted",
  odata: exceptedOdata,
  cast: (filter) => ({
    ...getDefaultCast(exceptedFilter.type)(filter),
    value: filter.value === "true",
  }),
  parse: (filter: Filter): FormFilter => {
    return {
      ...filter,
      value: filter.value === true ? "true" : "false",
      ors: filter.ors?.map((or) => ({
        operator: or.operator,
        value: or.value === true ? "true" : "false",
      })),
    };
  },
};

export const hasAgentOdata: OdataDefinition = {
  id: "has_agent",
  scope: "instances",
};

export const hasAgentFilter: AdditionalFilter = {
  id: getOdataId(hasAgentOdata),
  operators: ["is"],
  type: "boolean",
  label: "Has Agent",
  odata: hasAgentOdata,
  cast: (filter) => ({
    ...getDefaultCast(hasAgentFilter.type)(filter),
    value: filter.value === "true",
  }),
  parse: (filter) => {
    return {
      ...filter,
      value: filter.value === true ? "true" : "false",
      ors: filter.ors?.map((or) => ({
        operator: or.operator,
        value: or.value === true ? "true" : "false",
      })),
    };
  },
};

export const lastSeenOdata: OdataDefinition = {
  id: "last_seen",
  scope: "instances",
};

export const lastSeenFilter: AdditionalFilter = {
  id: getOdataId(lastSeenOdata),
  operators: ["less than", "greater than"],
  type: "date",
  label: "Last Seen",
  odata: lastSeenOdata,
};

export const lastScanOdata: OdataDefinition = {
  id: "last_scan",
  scope: "instances",
};

export const lastScanFilter: AdditionalFilter = {
  id: getOdataId(lastScanOdata),
  operators: ["less than", "greater than"],
  type: "date",
  label: "Last Scan",
  odata: lastScanOdata,
};

export const ownershipOdata: OdataDefinition = {
  id: "device_ownership",
  scope: "instances",
};

export const ownershipFilter: AdditionalFilter = {
  id: getOdataId(ownershipOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Ownership",
  odata: ownershipOdata,
};

export const platformOdata: OdataDefinition = {
  id: "platform",
  scope: "instances",
};

export const platformFilter: AdditionalFilter = {
  id: getOdataId(platformOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Platform",
  odata: platformOdata,
};

export const versionOdata: OdataDefinition = {
  id: "version",
  scope: "instances",
};

export const versionFilter: AdditionalFilter = {
  id: getOdataId(versionOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Version",
  odata: versionOdata,
};

export const deviceTypeOdata: OdataDefinition = {
  id: "device_type",
  scope: "instances",
};

export const deviceTypeFilter: AdditionalFilter = {
  id: getOdataId(deviceTypeOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Device Type",
  odata: deviceTypeOdata,
};

export const userTypeOdata: OdataDefinition = {
  id: "user_type",
  scope: "instances",
};

export const userTypeFilter: AdditionalFilter = {
  id: getOdataId(userTypeOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "User Type",
  odata: userTypeOdata,
};

export const inboxTypeOdata: OdataDefinition = {
  id: "inbox_type",
  scope: "instances",
};

export const inboxTypeFilter: AdditionalFilter = {
  id: getOdataId(inboxTypeOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Inbox Type",
  odata: inboxTypeOdata,
};

export const domainOdata: OdataDefinition = {
  id: "domain",
  scope: "instances",
};

export const domainFilter: AdditionalFilter = {
  id: getOdataId(domainOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Domain",
  odata: domainOdata,
};

export const principalNameOdata: OdataDefinition = {
  id: "principal_name",
  scope: "instances",
};

export const principalNameFilter: AdditionalFilter = {
  id: getOdataId(principalNameOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Principal Name",
  odata: principalNameOdata,
};

export const statusOdata: OdataDefinition = {
  id: "status",
  scope: "instances",
};

export const statusFilter: AdditionalFilter = {
  id: getOdataId(statusOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Status",
  odata: statusOdata,
};

export const riskOdata: OdataDefinition = {
  id: "risk",
  scope: "instances",
};

export const riskFilter: AdditionalFilter = {
  id: getOdataId(riskOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Risk",
  odata: riskOdata,
};

export const emailOdata: OdataDefinition = {
  id: "email",
};

export const emailFilter: AdditionalFilter = {
  id: getOdataId(emailOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Email",
  odata: emailOdata,
};

export const hostNameOdata: OdataDefinition = {
  id: "normalized_hostname",
};

export const hostNameFilter: AdditionalFilter = {
  id: getOdataId(hostNameOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Hostname",
  odata: hostNameOdata,
};

export const serialNumberOdata: OdataDefinition = {
  id: "normalized_serial_number",
};

export const serialNumberFilter: AdditionalFilter = {
  id: getOdataId(serialNumberOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Serial Number",
  odata: serialNumberOdata,
};

export const cloudAccountIdOdata: OdataDefinition = {
  id: "normalized_cloud_account_id",
};

export const cloudAccountIdFilter: AdditionalFilter = {
  id: getOdataId(cloudAccountIdOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Cloud Account ID",
  odata: cloudAccountIdOdata,
};

export const cloudInstanceIdOdata: OdataDefinition = {
  id: "normalized_cloud_instance_id",
};

export const cloudInstanceIdFilter: AdditionalFilter = {
  id: getOdataId(cloudInstanceIdOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Cloud Instance ID",
  odata: cloudInstanceIdOdata,
};

export const macAddressOdata: OdataDefinition = {
  id: "normalized_mac_address",
};

export const macAddressFilter: AdditionalFilter = {
  id: getOdataId(macAddressOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "MAC Address",
  odata: macAddressOdata,
  isArray: true,
};

export const controlCloudAccountIdOdata: OdataDefinition = {
  id: "cloud_account_id",
  scope: "instances",
};

export const controlCloudAccountIdFilter: AdditionalFilter = {
  id: getOdataId(controlCloudAccountIdOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Cloud Account ID",
  odata: controlCloudAccountIdOdata,
};

export const controlCloudInstanceIdOdata: OdataDefinition = {
  id: "cloud_instance_id",
  scope: "instances",
};

export const controlCloudInstanceIdFilter: AdditionalFilter = {
  id: getOdataId(controlCloudInstanceIdOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Cloud Instance ID",
  odata: controlCloudInstanceIdOdata,
};

export const controlCloudRegionOdata: OdataDefinition = {
  id: "cloud_region",
  scope: "instances",
};

export const controlCloudRegionFilter: AdditionalFilter = {
  id: getOdataId(controlCloudRegionOdata),
  operators: ["is", "is not", "contains", "matches", "does not match"],
  type: "text",
  label: "Cloud Region",
  odata: controlCloudRegionOdata,
};

export const userDevicesOdata: OdataDefinition = {
  id: "devices",
  sort: ["length(devices)", "email"],
};
export const userDevicesFilter: AdditionalFilter = {
  id: getOdataId(userDevicesOdata),
  operators: ["is", "is not", "less than", "greater than"],
  type: "number",
  label: "Number of Devices",
  odata: userDevicesOdata,
  cast: (filter) => ({
    ...getDefaultCast(userDevicesFilter.type)(filter),
    fn: "length",
  }),
};

export const startTimeOdata: OdataDefinition = {
  id: "start_time",
  scope: "failures",
};

export const rfmOData: OdataDefinition = {
  id: "reduced_functionality_mode",
  scope: "instances",
};
export const rfmFilter: AdditionalFilter = {
  id: getOdataId(rfmOData),
  operators: ["is"],
  type: "boolean",
  label: "Reduced Functionality",
  odata: rfmOData,
  group: "Control Failures",
};

export const noEDROData: OdataDefinition = {
  id: "missing_edr",
};
export const noEDRFilter: AdditionalFilter = {
  id: getOdataId(noEDROData),
  operators: ["is"],
  type: "boolean",
  label: "No EDR",
  odata: noEDROData,
  group: "Control Failures",
};

export const noEndpointManagementOData: OdataDefinition = {
  id: "missing_asset_manager",
};
export const noEndpointManagementFilter: AdditionalFilter = {
  id: getOdataId(noEndpointManagementOData),
  operators: ["is"],
  type: "boolean",
  label: "No Endpoint Management",
  odata: noEndpointManagementOData,
  group: "Control Failures",
};

export const noVulnerabilityManagementOData: OdataDefinition = {
  id: "missing_vuln_manager",
};
export const noVulnerabilityManagementFilter: AdditionalFilter = {
  id: getOdataId(noVulnerabilityManagementOData),
  operators: ["is"],
  type: "boolean",
  label: "No Vulnerability Management",
  odata: noVulnerabilityManagementOData,
  group: "Control Failures",
};

export const missingScanOData: OdataDefinition = {
  id: "missing_scan",
  scope: "instances",
};
export const missingScanFilter: AdditionalFilter = {
  id: getOdataId(missingScanOData),
  operators: ["is"],
  type: "boolean",
  label: "Missing Vulnerability Scan",
  odata: missingScanOData,
  group: "Control Failures",
};

export const outOfDateScanOData: OdataDefinition = {
  id: "out_of_date_scan",
  scope: "instances",
};
export const outOfDateScanFilter: AdditionalFilter = {
  id: getOdataId(outOfDateScanOData),
  operators: ["is"],
  type: "boolean",
  label: "Out-of-Date Vulnerability Scan",
  odata: outOfDateScanOData,
  group: "Control Failures",
};

export const missingMFAOData: OdataDefinition = {
  id: "missing_mfa",
  scope: "instances",
};
export const missingMFAFilter: AdditionalFilter = {
  id: getOdataId(missingMFAOData),
  operators: ["is"],
  type: "boolean",
  label: "Missing MFA",
  odata: missingMFAOData,
  group: "Control Failures",
};

export const rfmStartTimeOdata: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.REDUCED_FUNCTIONALITY_MODE },
};
export const mfaStartTimeOdata: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.MISSING_MFA },
};
export const noAMStartTimeOdata: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.NO_ASSET_MANAGER },
};
export const noEDRStartTimeOData: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.NO_EDR },
};
export const noVMStartTimeOdata: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.NO_VULN_MANAGER },
};

export const startTimeFilter: AdditionalFilter = {
  id: getOdataId(startTimeOdata),
  operators: ["less than", "greater than"],
  type: "duration",
  label: "Days in State",
  odata: startTimeOdata,
};

export function getOdataDurationString(value: OdataValue) {
  if (typeof value === "boolean" || typeof value === "object") {
    throw new TypeError(
      "Expected filter value to be a string or number when casting",
    );
  }
  let totalSeconds =
    typeof value === "string" ? Number.parseInt(value, 10) : value;
  const days = Math.floor(totalSeconds / secondsInDay);
  totalSeconds -= days * secondsInDay;
  const hours = Math.floor(totalSeconds / secondsInHour);
  totalSeconds -= hours * secondsInHour;
  const minutes = Math.floor(totalSeconds / secondsInMinute);
  totalSeconds -= minutes * secondsInMinute;
  const seconds = totalSeconds;
  return `P${days}DT${hours}H${minutes}M${seconds}S`;
}

export function getDurationLastSeenSeconds(value: OdataValue) {
  if (typeof value !== "string") {
    throw new TypeError("Expected filter value to be a string");
  }
  const regex = /duration'P(\d+D)?T?(\d+H)?(\d+M)?(\d+S)?'/;
  const matches = value.match(regex);
  if (!matches || !matches[1]) {
    throw new Error("Unable to parse filter string");
  }

  const days = Number.parseInt(matches[1] ?? "0", 10);
  const hours = Number.parseInt(matches[2] ?? "0", 10);
  const minutes = Number.parseInt(matches[3] ?? "0", 10);
  const seconds = Number.parseInt(matches[4] ?? "0", 10);

  const totalSeconds = Math.floor(
    days * secondsInDay +
      hours * secondsInHour +
      minutes * secondsInMinute +
      seconds,
  );

  return totalSeconds;
}

export function flipOperator(operator: string) {
  return operator === "greater than" ? "less than" : "greater than";
}

export function prefixDurationId(id: string) {
  return "duration_" + id;
}

export function isDurationId(id: string) {
  return id.includes("duration_");
}

export function convertDurationFilter(
  odata: OdataDefinition,
  operator: OdataOperator,
  value: number,
): Filter {
  return {
    ...odata,
    operator,
    value: {
      type: "raw",
      value: `now() sub duration'${getOdataDurationString(value)}'`,
    },
  };
}

export function convertDurationAdditionalFilter(
  filter: AdditionalFilter,
  odata: OdataDefinition,
): AdditionalFilter {
  return {
    ...filter,
    id: getOdataId({ ...odata, id: prefixDurationId(odata.id) }),
    cast: (filter) => ({
      ...filter,
      operator: flipOperator(filter.operator),
      value: {
        type: "raw",
        value: `now() sub duration'${getOdataDurationString(filter.value)}'`,
      },
    }),
    parse: (filter) => {
      if (typeof filter.value !== "object" || filter.value.type !== "raw") {
        throw new TypeError(
          "Expected duration filter value to be a raw odata object",
        );
      }
      return {
        ...filter,
        operator: flipOperator(filter.operator),
        value: getDurationLastSeenSeconds(filter.value.value).toString(),
        ors: filter.ors?.map((or) => {
          if (typeof or.value !== "object" || or.value.type !== "raw") {
            throw new TypeError(
              "Expected duration filter value to be a raw odata object",
            );
          }
          return {
            ...or,
            operator: flipOperator(or.operator),
            value: getDurationLastSeenSeconds(or.value.value).toString(),
          };
        }),
      };
    },
  };
}

export const durationLastSeenFilter: AdditionalFilter =
  convertDurationAdditionalFilter(
    {
      id: getOdataId(lastSeenOdata),
      operators: ["less than", "greater than"],
      type: "duration",
      label: "Days since Last Seen",
      odata: lastSeenOdata,
    },
    lastSeenOdata,
  );

export const durationStartTimeFilter: AdditionalFilter =
  convertDurationAdditionalFilter(
    {
      id: getOdataId(startTimeOdata),
      operators: ["less than", "greater than"],
      type: "duration",
      label: "Days in State",
      odata: startTimeOdata,
    },
    startTimeOdata,
  );

const prefixAny = (filter: AdditionalFilter): AdditionalFilter => ({
  ...filter,
  label: `Any ${filter.label}`,
});

export const anyOwnershipFilter = prefixAny(ownershipFilter);
export const anyPlatformFilter = prefixAny(platformFilter);
export const anyDeviceTypeFilter = prefixAny(deviceTypeFilter);
export const anyUserTypeFilter = prefixAny(userTypeFilter);
export const anyInboxTypeFilter = prefixAny(inboxTypeFilter);
export const anyLastSeenFilter = prefixAny(lastSeenFilter);
export const anyStatusFilter = prefixAny(statusFilter);
export const anyRiskFilter = prefixAny(riskFilter);
export const anyDomainFilter = prefixAny(domainFilter);
export const anyPrincipalNameFilter = prefixAny(principalNameFilter);
export const anyCloudRegionFilter = prefixAny(controlCloudRegionFilter);
export const anyDurationLastSeenFilter = {
  ...durationLastSeenFilter,
  label: "Days since Any Last Seen",
};

export function endpointControlStartTimeFilters({
  category,
  controls,
  endpointControls,
}: {
  category: ValidControlCategory;
  controls: ValidControl[];
  endpointControls: Map<string, ControlMonitorEndpointControlSummary>;
}): AdditionalFilter[] {
  return [
    ...controls.flatMap((control) => {
      const endpointControl = endpointControls.get(
        `${control.id}-${control.instance_id}`,
      );
      const eventCodes = [
        ...getDefensivePartnerEventCodes(endpointControl),
        ...getVulnerabilityPartnerEventCodes(endpointControl),
      ];
      return eventCodes.map((event) => {
        const odata: OdataDefinition = {
          ...startTimeOdata,
          with: {
            control: control.id,
            instance_id: control.instance_id,
            event,
          },
        };
        return convertDurationAdditionalFilter(
          {
            ...startTimeFilter,
            id: getOdataId(odata),
            label: prefixControlColumnLabel(
              `Days in ${getPartnerEventName(event)}`,
              control,
            ),
            group: getNamedControl(control),
            odata,
          },
          odata,
        );
      });
    }),
    ...getEndpointPartnerEventCodes(category).map((event) => {
      const odata: OdataDefinition = {
        ...startTimeOdata,
        with: { event },
      };
      return convertDurationAdditionalFilter(
        {
          ...startTimeFilter,
          id: getOdataId(odata),
          label: `Days in ${getPartnerEventName(event)}`,
          odata,
        },
        odata,
      );
    }),
  ];
}

export function identityControlStartTimeFilters(controls: ValidControl[]) {
  return controls.flatMap((control) => {
    return identityProviderPartnerEventCodes.map((event) => {
      const odata: OdataDefinition = {
        ...startTimeOdata,
        with: { control: control.id, instance_id: control.instance_id, event },
      };
      return convertDurationAdditionalFilter(
        {
          ...startTimeFilter,
          id: getOdataId(odata),
          label: prefixControlColumnLabel(getPartnerEventName(event), control),
          group: getNamedControl(control),
          odata,
        },
        odata,
      );
    });
  });
}

export const controlDeviceTypeFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter(
      (control) =>
        isDefensiveControl(control.id) ||
        isDiscoveredDevicesControl(control.id) ||
        isEndpointManagementControl(control.id) ||
        isVulnerabilityManagementControl(control.id),
    )
    .map((control) => {
      const odata: OdataDefinition = {
        ...deviceTypeOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...deviceTypeFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(deviceTypeFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlUserTypeFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter((control) => isIdentityProviderControl(control.id))
    .map((control) => {
      const odata: OdataDefinition = {
        ...userTypeOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...userTypeFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(userTypeFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlInboxTypeFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter((control) => isEmailControl(control.id))
    .map((control) => {
      const odata: OdataDefinition = {
        ...inboxTypeOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...inboxTypeFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(inboxTypeFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlLastSeenFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls.map((control) => {
    const odata: OdataDefinition = {
      ...lastSeenOdata,
      with: { control: control.id, instance_id: control.instance_id },
    };
    return {
      ...lastSeenFilter,
      id: getOdataId(odata),
      label: prefixControlColumnLabel(lastSeenFilter.label, control),
      group: getNamedControl(control),
      odata,
    };
  });
};

export const controlLastScanFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter((c) => isVulnerabilityManagementControl(c.id))
    .map((control) => {
      const odata: OdataDefinition = {
        ...lastScanOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...lastScanFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(lastScanFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlPlatformFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter(
      (control) =>
        isDefensiveControl(control.id) ||
        isDiscoveredDevicesControl(control.id) ||
        isEndpointManagementControl(control.id) ||
        isVulnerabilityManagementControl(control.id),
    )
    .map((control) => {
      const odata: OdataDefinition = {
        ...platformOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...platformFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(platformFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlVersionFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter(
      (control) =>
        isDefensiveControl(control.id) ||
        isDiscoveredDevicesControl(control.id),
    )
    .map((control) => {
      const odata: OdataDefinition = {
        ...versionOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...versionFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(versionFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlOwnershipFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter(
      (control) =>
        isEndpointManagementControl(control.id) ||
        isDiscoveredDevicesControl(control.id),
    )
    .map((control) => {
      const odata: OdataDefinition = {
        ...ownershipOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...ownershipFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(ownershipFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlHasAgentFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter((c) => isVulnerabilityManagementControl(c.id))
    .map((control) => {
      const odata: OdataDefinition = {
        ...hasAgentOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...hasAgentFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(hasAgentFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlRiskFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter((control) => isIdentityProviderControl(control.id))
    .map((control) => {
      const odata: OdataDefinition = {
        ...riskOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...riskFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(riskFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlStatusFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter((control) => isIdentityProviderControl(control.id))
    .map((control) => {
      const odata: OdataDefinition = {
        ...statusOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...statusFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(statusFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlDomainFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter((control) => isEmailControl(control.id))
    .map((control) => {
      const odata: OdataDefinition = {
        ...domainOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...domainFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(domainFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

export const controlPrincipalNamesFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls
    .filter((control) => isEmailControl(control.id))
    .map((control) => {
      const odata: OdataDefinition = {
        ...principalNameOdata,
        with: { control: control.id, instance_id: control.instance_id },
      };
      return {
        ...principalNameFilter,
        id: getOdataId(odata),
        label: prefixControlColumnLabel(principalNameFilter.label, control),
        group: getNamedControl(control),
        odata,
      };
    });
};

function isCloudSupportedControl(c: ValidControl) {
  return (
    isVulnerabilityManagementControl(c.id) ||
    isDefensiveControl(c.id) ||
    isDiscoveredDevicesControl(c.id) ||
    isEndpointManagementControl(c.id)
  );
}

export const controlCloudFilters = (
  controls: ValidControl[],
): AdditionalFilter[] => {
  return controls.filter(isCloudSupportedControl).flatMap((control) => {
    const cloudRegionOdata: OdataDefinition = {
      ...controlCloudRegionOdata,
      with: { control: control.id, instance_id: control.instance_id },
    };
    const cloudAccountIdOdata: OdataDefinition = {
      ...controlCloudAccountIdOdata,
      with: { control: control.id, instance_id: control.instance_id },
    };
    const cloudInstanceIdOdata: OdataDefinition = {
      ...controlCloudInstanceIdOdata,
      with: { control: control.id, instance_id: control.instance_id },
    };
    return [
      {
        ...controlCloudRegionFilter,
        id: getOdataId(cloudRegionOdata),
        label: prefixControlColumnLabel(
          controlCloudRegionFilter.label,
          control,
        ),
        group: getNamedControl(control),
        odata: cloudRegionOdata,
      },
      {
        ...controlCloudAccountIdFilter,
        id: getOdataId(cloudAccountIdOdata),
        label: prefixControlColumnLabel(
          controlCloudAccountIdFilter.label,
          control,
        ),
        group: getNamedControl(control),
        odata: cloudAccountIdOdata,
      },
      {
        ...controlCloudInstanceIdFilter,
        id: getOdataId(cloudInstanceIdOdata),
        label: prefixControlColumnLabel(
          controlCloudInstanceIdFilter.label,
          control,
        ),
        group: getNamedControl(control),
        odata: cloudInstanceIdOdata,
      },
    ];
  });
};
